<script setup lang="ts">
import { provide, ref } from 'vue';
import AppAside from '@/components/AppAside.vue';
import AppBreadcrumb from '@/components/AppBreadcrumb.vue';
import { RELOAD_VIEW } from '@/composables/reloadView';

/**
 * Re-mount key for the active view. Switching menu options already swaps the
 * route (so content updates), but clicking the *already-active* option fires no
 * navigation — so the page would never refresh on a re-click. The Aside calls
 * the provided reloader, which bumps this nonce; the `:key` change forces the
 * current view to re-mount (and thus re-fetch its data).
 */
const viewNonce = ref(0);
provide(RELOAD_VIEW, () => {
  viewNonce.value += 1;
});
</script>

<template>
  <div class="qb-app">
    <AppAside class="qb-app__aside" />
    <main class="qb-app__body">
      <AppBreadcrumb class="qb-app__breadcrumb" />
      <div class="qb-app__content">
        <router-view :key="`view-${viewNonce}`" />
      </div>
    </main>
  </div>
</template>

<style scoped>
/* Layout: Aside is fixed-height (no page scroll), Body scrolls internally.
   When a long form opens (e.g. PermissionsTree with 24 rows), only the body
   content scrolls — Aside, logo, user-card, menu, locale switcher all stay
   in view. Page itself NEVER scrolls. */

.qb-app {
  display: flex;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  background: var(--qb-surface-ground);
}

.qb-app__aside {
  flex: 0 0 var(--qb-aside-width);
  width: var(--qb-aside-width);
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.qb-app__body {
  flex: 1 1 auto;
  min-width: 0;
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: var(--qb-content-padding-y) var(--qb-content-padding-x);
  gap: 16px;
  overflow: hidden;
}

.qb-app__breadcrumb {
  flex: 0 0 auto;
}

.qb-app__content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  /* No scroll on the content area itself — the active view decides which of
     its own subsections scroll (top form stays sticky, lists scroll). */
  overflow: hidden;
}

/* The active view (PackagesView / PackageEditView / PlaceholderView) should
   fill the content area. Each is responsible for its own internal scroll. */
.qb-app__content > * {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
}
</style>
